<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onScopeDispose,
  toRefs,
  watch,
  watchEffect,
} from 'vue';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkPolyDataNormals from '@kitware/vtk.js/Filters/Core/PolyDataNormals';
import vtkWindowedSincPolyDataFilter from '@kitware/vtk.js/Filters/General/WindowedSincPolyDataFilter';
import { useGeometryRepresentation } from '@/src/core/vtk/useGeometryRepresentation';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useModelStore } from '@/src/store/datasets-models';
import { usePycadUiStore } from '@/src/store/pycad-ui';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useCropStore } from '@/src/store/tools/crop';
import { storeToRefs } from 'pinia';

interface Props {
  modelId: string;
}

const props = defineProps<Props>();
const { modelId } = toRefs(props);

const view = inject(VtkViewContext);
if (!view) throw new Error('No VtkView');
const vtkView = view;

const modelStore = useModelStore();
const pycadUi = usePycadUiStore();
const { volumeRenderingEnabled } = storeToRefs(pycadUi);

const rawPolyData = computed<vtkPolyData | undefined>(
  () => modelStore.dataIndex[modelId.value]
);

// Two-stage surface refinement so meshes generated by marching-cubes-style
// segmentation pipelines render as anatomically smooth surfaces:
//
// 1) WindowedSinc smoothing low-passes the vertex positions, removing the
//    voxel-grid stair-step ridges that survive even fine triangulation.
//    `featureEdgeSmoothing=false` preserves sharp anatomical features (e.g.
//    tooth cusps); `passBand=0.1` is gentle enough to keep detail but kills
//    high-frequency voxel artifacts.
// 2) PolyDataNormals computes per-vertex normals so Gouraud shading is
//    continuous across triangles. Without this, decimated meshes show every
//    triangle as a flat-shaded facet.
const sincFilter = vtkWindowedSincPolyDataFilter.newInstance({
  numberOfIterations: 20,
  passBand: 0.1,
  featureAngle: 45,
  featureEdgeSmoothing: false,
  boundarySmoothing: true,
  nonManifoldSmoothing: false,
  normalizeCoordinates: true,
});

const normalsFilter = vtkPolyDataNormals.newInstance({
  computePointNormals: true,
  computeCellNormals: false,
  splitting: false,
  consistency: true,
});

onScopeDispose(() => {
  sincFilter.delete();
  normalsFilter.delete();
});

// Meshes beyond this size skip WindowedSinc smoothing: the filter runs
// synchronously on the main thread and large STLs would freeze the UI
// (or exhaust memory and crash the tab).
const MAX_SMOOTH_POINTS = 400_000;

const smoothedPolyData = computed<vtkPolyData | undefined>(() => {
  const data = rawPolyData.value;
  if (!data) return undefined;
  try {
    const nPoints = data.getPoints()?.getNumberOfPoints() ?? 0;
    if (nPoints === 0) return undefined;

    let surface: vtkPolyData = data;
    if (nPoints <= MAX_SMOOTH_POINTS) {
      sincFilter.setInputData(data);
      surface = sincFilter.getOutputData() as vtkPolyData;
    }
    normalsFilter.setInputData(surface);
    return normalsFilter.getOutputData() as vtkPolyData;
  } catch (err) {
    console.warn('Mesh smoothing failed; rendering raw polydata.', err);
    return data;
  }
});

const rep = useGeometryRepresentation(vtkView, smoothedPolyData);

// Apply the same VOI crop planes as the volume renderer. Planes must be
// (re)applied whenever mesh data or crop bounds change so the OpenGL mapper
// rebuilds its clipping shaders after setInputData().
const { currentImageID } = useCurrentImage();
const cropStore = useCropStore();
const croppingPlanes = cropStore.getComputedVTKPlanes(currentImageID);

function applyMeshCropping() {
  if (!smoothedPolyData.value) {
    rep.mapper.removeAllClippingPlanes();
    return;
  }

  const planes = croppingPlanes.value;
  rep.mapper.removeAllClippingPlanes();
  if (planes?.length) {
    planes.forEach((plane) => rep.mapper.addClippingPlane(plane));
  }
  vtkView.requestRender();
}

watch([smoothedPolyData, croppingPlanes], () => nextTick(applyMeshCropping), {
  immediate: true,
  flush: 'post',
});

const displayConfig = computed(() => modelStore.display[modelId.value]);

watchEffect(() => {
  if (!smoothedPolyData.value) return;
  const display =
    displayConfig.value ?? modelStore.getModelDisplay(modelId.value);
  rep.mapper.setScalarVisibility(false);
  rep.property.setColor(...display.color);
  rep.property.setOpacity(display.opacity);
  rep.actor.setVisibility(display.visible);
  const vrOn = volumeRenderingEnabled.value;
  rep.property.setAmbient(vrOn ? 0.2 : 0.35);
  rep.property.setDiffuse(vrOn ? 0.8 : 0.65);
  rep.property.setSpecular(0.25);
  rep.property.setSpecularPower(20);
  // With VR off, cull back faces so thin shells do not show a hollow interior.
  rep.property.setBackfaceCulling(!vrOn);
  vtkView.requestRender();
});

defineExpose(rep);
</script>

<template>
  <slot></slot>
</template>
